import React, { useState, useEffect } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { MetricCard } from '@/components/common/MetricCard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Coins, TrendingUp, TrendingDown, Gift } from 'lucide-react';
import API from '@/api/client';
import { formatNumber, formatDateTime } from '@/utils/helpers';
import { toast } from 'sonner';

const AdminPoints = () => {
  const [loading, setLoading] = useState(true);
  const [pointsData, setPointsData] = useState(null);

  useEffect(() => {
    fetchPointsData();
  }, []);

  const fetchPointsData = async () => {
    try {
      setLoading(true);
      const response = await API.get('/admin/points/overview');
      setPointsData(response.data);
    } catch (error) {
      console.error('Error fetching points data:', error);
      toast.error('Failed to load points overview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading points overview..." />;
  }

  if (!pointsData) {
    return <div className="text-center py-12">Failed to load data</div>;
  }

  const totalIssued = pointsData.recent_transactions
    .filter(t => t.transaction_type === 'earn')
    .reduce((sum, t) => sum + t.points, 0);
  
  const totalRedeemed = Math.abs(
    pointsData.recent_transactions
      .filter(t => t.transaction_type === 'redeem')
      .reduce((sum, t) => sum + t.points, 0)
  );

  const breakageRate = totalIssued > 0 ? ((totalIssued - totalRedeemed) / totalIssued * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50 px-4 md:px-6 py-4">
        <div className="flex items-center gap-4">
          <img
            src="https://customer-assets.emergentagent.com/job_e65b2a0c-b7a8-46e9-8a92-d5df4207b5ae/artifacts/8vn39jol_Physiomobile%20Logo.jpeg"
            alt="Physiomobile Logo"
            className="h-10"
          />
          <h1 className="font-heading text-2xl font-bold text-gray-800">Flywheel HQ</h1>
        </div>
      </header>

      <main className="px-4 md:px-6 lg:px-8 py-8">
        <PageHeader
          title="Points & Rewards Management"
          subtitle="Monitor loyalty program and redemption activity"
          actions={
            <Button onClick={() => window.location.href = '/admin/dashboard'} variant="outline">
              Back to Dashboard
            </Button>
          }
        />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <MetricCard
            title="Active Wallets"
            value={formatNumber(pointsData.total_wallets)}
            subtitle="Users with points"
            icon={Coins}
            testId="metric-total-wallets"
          />

          <MetricCard
            title="Total Balance"
            value={formatNumber(pointsData.total_balance)}
            subtitle="Points in circulation"
            icon={TrendingUp}
            testId="metric-total-balance"
          />

          <MetricCard
            title="Average Balance"
            value={formatNumber(pointsData.avg_balance)}
            subtitle="Per wallet"
            icon={Gift}
            testId="metric-avg-balance"
          />

          <MetricCard
            title="Breakage Rate"
            value={`${breakageRate.toFixed(1)}%`}
            subtitle="Unredeemed points"
            icon={TrendingDown}
            testId="metric-breakage-rate"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6" data-testid="tier-distribution-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Tier Distribution
            </h2>
            <div className="space-y-3">
              {pointsData.tier_distribution.map((tier) => (
                <div key={tier._id} className="flex items-center justify-between">
                  <span className="font-medium capitalize">{tier._id} Tier</span>
                  <span className="font-bold text-primary">{tier.count}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6" data-testid="recent-transactions-card">
            <h2 className="font-heading text-2xl font-semibold mb-4 text-gray-800">
              Recent Transactions
            </h2>
            <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
              {pointsData.recent_transactions.slice(0, 10).map((tx, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium">{tx.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(tx.created_at)}
                    </p>
                  </div>
                  <span
                    className={`font-bold ${
                      tx.points > 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {tx.points > 0 && '+'}
                    {tx.points}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPoints;
